import { CloseRounded, GitHub, LinkedIn } from '@mui/icons-material';
import { Modal } from '@mui/material';
import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
width: 100%;
height: 100%;
position: absolute;
top: 0;
left: 0;
background-color: #000000a7;
display: flex;
align-items: top;
justify-content: center;
overflow-y: scroll;
transition: all 0.5s ease;
`;

const Wrapper = styled.div`
max-width: 800px;
width: 100%;
border-radius: 16px;
margin: 50px 12px;
height: min-content;
background-color: ${({ theme }) => theme.card};
color: ${({ theme }) => theme.text_primary || '#fff'};
padding: 20px;
display: flex;
flex-direction: column;
position: relative;
`;

const Title = styled.div`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin: 8px 6px 0px 6px;
  @media only screen and (max-width: 600px) {
      font-size: 20px;
      margin: 6px 6px 0px 6px;
  }
`;

const Date = styled.div`
    font-size: 14px;
    margin: 2px 6px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary};
    @media only screen and (max-width: 768px){
        font-size: 12px;
    }
`;

const Desc = styled.div`
    font-size: 16px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_primary};
    margin: 8px 6px;
    line-height: 1.6;
    @media only screen and (max-width: 600px) {
        font-size: 14px;
        margin: 6px 6px;
    }
`;

const Image = styled.img`
    width: 100%;
    object-fit: cover;
    border-radius: 12px;
    margin-top: 30px;
    box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.3);
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 200px;
    background: linear-gradient(135deg, #1f2634 0%, #2b1055 100%);
    border-radius: 12px;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #b1b2b3;
    font-size: 14px;
    border: 1px dashed rgba(255, 255, 255, 0.2);
    gap: 6px;
`;

const Label = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    margin: 8px 6px;
    @media only screen and (max-width: 600px) {
        font-size: 16px;
        margin: 8px 6px;
    }
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 8px 0px;
    @media only screen and (max-width: 600px) {
        margin: 4px 0px;
    }
`;

const Tag = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.primary};
    margin: 4px;
    padding: 4px 8px;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.primary + '20'};
    @media only screen and (max-width: 600px) {
        font-size: 12px;
    }
`;

const Members = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-wrap: wrap;
    margin: 12px 6px;
    @media only screen and (max-width: 600px) {
        margin: 4px 6px;
    }
`;

const Member = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const MemberImage = styled.img`
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 50%;
    margin-bottom: 4px;
    box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.3);
    @media only screen and (max-width: 600px) {
        width: 32px;
        height: 32px;
    }
`;

const MemberName = styled.div`
    font-size: 16px;
    font-weight: 500;
    width: 200px;
    color: ${({ theme }) => theme.text_primary};
    @media only screen and (max-width: 600px) {
        font-size: 14px;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 16px 0px 8px 0px;
    gap: 12px;
    @media only screen and (max-width: 600px) {
        flex-direction: column;
    }
`;

const Button = styled.a`
    width: 100%;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    padding: 12px 16px;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.primary};
    ${({ dull, theme }) => dull && `
        background-color: ${theme.bgLight || '#1c1e27'};
        color: ${theme.text_secondary};
        &:hover {
            background-color: ${theme.bg + '99' || '#242735'};
        }
    `}
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background-color: ${({ theme }) => theme.primary + 'cc'};
    }
    @media only screen and (max-width: 600px) {
        font-size: 13px;
        padding: 10px 12px;
    }
`;

const ProjectDetailsModal = ({ openModal, setOpenModal }) => {
    const project = openModal?.project;
    if (!project) return null;

    return (
        <Modal open={true} onClose={() => setOpenModal({ state: false, project: null })}>
            <Container>
                <Wrapper>
                    <CloseRounded
                        style={{
                            position: "absolute",
                            top: "14px",
                            right: "18px",
                            cursor: "pointer",
                            color: "inherit"
                        }}
                        onClick={() => setOpenModal({ state: false, project: null })}
                    />
                    {project.img ? (
                        <Image src={project.img} alt={project.title} />
                    ) : (
                        <ImagePlaceholder>
                            <span style={{ fontSize: 16, fontWeight: 600 }}>{project.title}</span>
                            <span style={{ color: '#854CE6' }}>TODO: Add project screenshot / preview</span>
                        </ImagePlaceholder>
                    )}
                    <Title>{project.title}</Title>
                    <Date>{project.date}</Date>
                    <Tags>
                        {project.tags?.map((tag, idx) => (
                            <Tag key={idx}>{tag}</Tag>
                        ))}
                    </Tags>
                    <Desc>{project.description}</Desc>
                    {project.member && (
                        <>
                            <Label>Members</Label>
                            <Members>
                                {project.member.map((member, idx) => (
                                    <Member key={idx}>
                                        <MemberImage src={member.img} alt={member.name} />
                                        <MemberName>{member.name}</MemberName>
                                        {member.github && (
                                            <a href={member.github} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                                <GitHub />
                                            </a>
                                        )}
                                        {member.linkedin && (
                                            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                                <LinkedIn />
                                            </a>
                                        )}
                                    </Member>
                                ))}
                            </Members>
                        </>
                    )}
                    <ButtonGroup>
                        {project.github && (
                            <Button dull href={project.github} target='_blank' rel='noopener noreferrer'>
                                View GitHub Code
                            </Button>
                        )}
                        {project.webapp ? (
                            <Button href={project.webapp} target='_blank' rel='noopener noreferrer'>
                                View Live App
                            </Button>
                        ) : (
                            <Button as="span" dull style={{ opacity: 0.7, cursor: 'default' }}>
                                Live Demo: TODO / Not Deployed
                            </Button>
                        )}
                    </ButtonGroup>
                </Wrapper>
            </Container>
        </Modal>
    );
};

export default ProjectDetailsModal;
